import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  IconButton,
  Divider,
  Stack,
} from "@mui/material";

import {
  Business as BusinessIcon,
  Edit as EditIcon,
  Language as LanguageIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon,
} from "@mui/icons-material";

import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import apiService from "../../service/Api";

interface CompanyDetails {
  id: number;
  name: string;
  company_type?: string;
  description?: string;
  company_logo?: string;
  phone_number?: string;
  primary_email?: string;
  secondary_email?: string;
  website?: string;
  street_address?: string;
  street_address_1?: string;
  street_address_2?: string;
  billing_address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  license_number?: string;
  specialties?: string;
  is_active: boolean;
  latitude?: string;
  longitude?: string;
  time_zone?: string;
}

const IndividualCompanyView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [companyData, setCompanyData] = useState<CompanyDetails | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const response = await apiService.get<CompanyDetails>(
          `api/companies/company/${id}/`,
        );

        setCompanyData(response.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchCompany();
  }, [id]);

  if (loading)
    return (
      <Box textAlign="center" mt={4}>
        <CircularProgress />
      </Box>
    );

  if (!companyData) return null;

  const fullAddress = [
    companyData.street_address,
    companyData.billing_address,
    companyData.city,
    companyData.state,
    companyData.postal_code,
    companyData.country,
  ]
    .filter((v) => v && v.trim())
    .join(", ");

  const LabelValue = ({
    label,
    value,
    type,
    color,
    openInGmail = false, // new optional prop
  }: {
    label: string;
    value?: string;
    type?: "email" | "website" | "phone";
    color?: string;
    openInGmail?: boolean;
  }) => {
    if (!value) {
      return (
        <Box>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="h6">-</Typography>
        </Box>
      );
    }

    let href = value;

    if (type === "email" && value) {
      if (openInGmail) {
        // Open Gmail compose window
        href = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(
          value.trim(),
        )}`;
      } else {
        href = `mailto:${value.trim()}`;
      }
    }

    if (type === "phone") href = `tel:${value}`;
    if (type === "website")
      href = value.startsWith("http") ? value : `https://${value}`;

    return (
      <Box>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>

        {type ? (
          <Typography
            component="a"
            href={href}
            target={
              type === "website" || (type === "email" && openInGmail)
                ? "_blank"
                : undefined
            }
            rel="noopener noreferrer"
            variant="h6"
            sx={{
              textDecoration: "none",
              color: color || "primary.main",
              cursor: "pointer",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            {value}
          </Typography>
        ) : (
          <Typography variant="h6">{value}</Typography>
        )}
      </Box>
    );
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      {/* FULL WIDTH HEADER ROW */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          width: "100%",
          mt: 1,
          pr: 4, // optional spacing from edge
        }}
      >
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/companies/${companyData.id}/edit`)}
        >
          Edit Company
        </Button>
      </Box>

      {/* CONSTRAINED CONTENT */}
      <Box
        sx={{
          maxWidth: 1200,
        }}
      >
        {/* SINGLE CARD WITH DIVIDER */}
        <Card sx={{ borderRadius: 3, boxShadow: 0 }}>
          <CardContent sx={{ p: 0 }}>
            <Grid container columns={12}>
              {/* LEFT PANEL */}
              <Grid
                size={{ xs: 12, md: 4 }}
                sx={{
                  textAlign: "center",
                  p: 4,
                }}
              >
                <Avatar
                  src={companyData.company_logo || ""}
                  sx={{
                    width: 130,
                    height: 130,
                    margin: "auto",
                    mb: 2,
                  }}
                >
                  <BusinessIcon fontSize="large" />
                </Avatar>

                <Typography variant="h5" fontWeight="bold">
                  {companyData.name}
                </Typography>

                <Typography color="text.secondary">
                  {companyData.company_type}
                </Typography>

                <Typography
                  mt={1}
                  color={companyData.is_active ? "success.main" : "error.main"}
                >
                  {companyData.is_active ? "Active" : "Inactive"}
                </Typography>

                <Divider sx={{ my: 2 }} />

                {/* Contact icons */}
                <Stack direction="row" justifyContent="center" spacing={2}>
                  {companyData.phone_number && (
                    <IconButton
                      component="a"
                      href={`tel:${companyData.phone_number}`}
                      sx={{ color: "#2E7D32" }}
                    >
                      <PhoneIcon />
                    </IconButton>
                  )}

                  {companyData.website && (
                    <IconButton
                      component="a"
                      href={
                        companyData.website.startsWith("http")
                          ? companyData.website
                          : `https://${companyData.website}`
                      }
                      target="_blank"
                      sx={{ color: "#6A1B9A" }}
                    >
                      <LanguageIcon />
                    </IconButton>
                  )}

                  {companyData.primary_email && (
                    <IconButton
                      component="a"
                      href={`https://mail.google.com/mail/?view=cm&fs=1&to=${companyData.primary_email}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: "#1565C0" }}
                    >
                      <EmailIcon />
                    </IconButton>
                  )}

                  {companyData.phone_number && (
                    <IconButton
                      component="a"
                      href={`https://wa.me/${companyData.phone_number.replace(/\D/g, "")}?text=${encodeURIComponent(
                        "Hi there!",
                      )}`}
                      target="_blank"
                      sx={{ color: "#25D366" }}
                    >
                      <WhatsAppIcon />
                    </IconButton>
                  )}
                </Stack>
              </Grid>

              {/* VERTICAL DIVIDER */}
              <Grid
                size={{ xs: 0, md: 0.2 }}
                sx={{
                  display: { xs: "none", md: "flex" },
                  justifyContent: "center",
                }}
              >
                <Divider orientation="vertical" flexItem />
              </Grid>
              {/* RIGHT PANEL */}
              <Grid size={{ xs: 12, md: 7.8 }} sx={{ p: 4 }}>
                <Grid container spacing={3}>
                  {/* Description */}
                  <Grid size={{ xs: 12 }}>
                    <LabelValue
                      label="Description"
                      value={companyData.description}
                    />
                  </Grid>

                  {/* Phone */}
                  <Grid size={{ xs: 12 }}>
                    <LabelValue
                      label="Phone"
                      value={companyData.phone_number}
                      type="phone"
                      color="#1565C0"
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <LabelValue
                      label="Primary Email"
                      value={companyData.primary_email}
                      type="email"
                      color="#1565C0"
                      openInGmail={true}
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <LabelValue
                      label="Secondary Email"
                      value={companyData.secondary_email}
                      type="email"
                      color="#1565C0"
                      openInGmail={true}
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <LabelValue
                      label="Website"
                      value={companyData.website}
                      type="website"
                      color="#1565C0"
                    />
                  </Grid>

                  {/* Address */}
                  <Grid size={{ xs: 12 }}>
                    <LabelValue label="Address" value={fullAddress} />
                  </Grid>

                  {/* Location */}
                  <Grid size={{ xs: 4 }}>
                    <LabelValue label="City" value={companyData.city} />
                  </Grid>

                  <Grid size={{ xs: 4 }}>
                    <LabelValue label="State" value={companyData.state} />
                  </Grid>

                  <Grid size={{ xs: 4 }}>
                    <LabelValue label="Country" value={companyData.country} />
                  </Grid>

                  {/* Other */}
                  <Grid size={{ xs: 6 }}>
                    <LabelValue
                      label="License Number"
                      value={companyData.license_number}
                    />
                  </Grid>

                  <Grid size={{ xs: 6 }}>
                    <LabelValue
                      label="Time Zone"
                      value={companyData.time_zone}
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <LabelValue
                      label="Service Specialties"
                      value={companyData.specialties}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </motion.div>
  );
};

export default IndividualCompanyView;

/* reusable component */
